import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { InstagramLogo, FacebookLogo, Envelope, Phone, MapPin, ShieldCheck } from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_calvi-fishing-book/artifacts/mw45n5je_Logo%20Calvi%20Fishing.png";

const PaymentIcons = ({ size = "normal" }) => {
  const iconClass = size === "small" ? "h-6" : "h-8";
  const containerClass = size === "small" ? "gap-2" : "gap-3";
  
  return (
    <div className={`flex items-center ${containerClass}`}>
      {/* Visa */}
      <div className={`${iconClass} px-2 bg-white rounded flex items-center justify-center`}>
        <svg viewBox="0 0 48 32" className={iconClass} xmlns="http://www.w3.org/2000/svg">
          <rect width="48" height="32" rx="4" fill="#fff"/>
          <path d="M19.5 21H17L18.8 11H21.3L19.5 21Z" fill="#1A1F71"/>
          <path d="M29.2 11.2C28.7 11 27.9 10.8 26.9 10.8C24.4 10.8 22.7 12 22.7 13.8C22.6 15.1 23.9 15.8 24.8 16.3C25.8 16.7 26.1 17 26.1 17.4C26.1 18 25.4 18.3 24.7 18.3C23.7 18.3 23.2 18.2 22.4 17.8L22.1 17.7L21.8 19.5C22.4 19.7 23.4 19.9 24.5 19.9C27.2 19.9 28.8 18.8 28.9 16.9C28.9 15.9 28.2 15.1 26.8 14.5C26 14.1 25.5 13.8 25.5 13.4C25.5 13 26 12.6 27 12.6C27.8 12.6 28.4 12.7 28.9 12.9L29.1 13L29.2 11.2Z" fill="#1A1F71"/>
          <path d="M33.2 11H31.2C30.6 11 30.1 11.2 29.9 11.8L26.2 21H28.9L29.4 19.5H32.7L33 21H35.4L33.2 11ZM30.2 17.7C30.4 17.2 31.3 14.8 31.3 14.8L31.8 17.7H30.2Z" fill="#1A1F71"/>
          <path d="M16.3 11L13.8 17.8L13.5 16.3C13 14.7 11.5 13 9.8 12.1L12.1 21H14.8L19 11H16.3Z" fill="#1A1F71"/>
          <path d="M12.2 11H8.1L8 11.2C11.2 12 13.3 13.8 14 16L13.2 11.8C13.1 11.2 12.6 11 12.2 11Z" fill="#F9A533"/>
        </svg>
      </div>
      {/* Mastercard */}
      <div className={`${iconClass} px-2 bg-white rounded flex items-center justify-center`}>
        <svg viewBox="0 0 48 32" className={iconClass} xmlns="http://www.w3.org/2000/svg">
          <rect width="48" height="32" rx="4" fill="#fff"/>
          <circle cx="19" cy="16" r="9" fill="#EB001B"/>
          <circle cx="29" cy="16" r="9" fill="#F79E1B"/>
          <path d="M24 9.3C25.9 10.8 27.2 13.2 27.2 16C27.2 18.8 25.9 21.2 24 22.7C22.1 21.2 20.8 18.8 20.8 16C20.8 13.2 22.1 10.8 24 9.3Z" fill="#FF5F00"/>
        </svg>
      </div>
      {/* Amex */}
      <div className={`${iconClass} px-2 bg-[#006FCF] rounded flex items-center justify-center`}>
        <span className="text-white font-bold text-[9px] leading-none tracking-tighter">AMEX</span>
      </div>
      {/* CB */}
      <div className={`${iconClass} px-2 bg-white rounded flex items-center justify-center border border-gray-200`}>
        <span className="font-bold text-[10px] leading-none tracking-tight" style={{background: 'linear-gradient(135deg, #1D4ED8, #059669)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'}}>CB</span>
      </div>
    </div>
  );
};

export { PaymentIcons };

export const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer className="bg-[#000000] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo & Description */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src={LOGO_URL} 
                alt="Calvi Fishing" 
                className="h-20 w-20 object-contain"
              />
            </div>
            <p className="text-gray-400 max-w-md leading-relaxed">
              {t('hero.subtitle')}
            </p>
            <div className="flex gap-4 mt-6">
              <a
                href="https://www.instagram.com/calvifishing/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#b7ede0]/20 flex items-center justify-center hover:bg-[#b7ede0] hover:text-[#000000] transition-colors"
                data-testid="footer-instagram"
              >
                <InstagramLogo size={20} />
              </a>
              <a
                href="https://www.facebook.com/calvifishing"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#b7ede0]/20 flex items-center justify-center hover:bg-[#b7ede0] hover:text-[#000000] transition-colors"
                data-testid="footer-facebook"
              >
                <FacebookLogo size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6 font-['Playfair_Display'] text-[#b7ede0]">Navigation</h4>
            <nav className="space-y-3">
              <Link to="/" className="block text-gray-400 hover:text-[#b7ede0] transition-colors">
                {t('nav.home')}
              </Link>
              <Link to="/trips" className="block text-gray-400 hover:text-[#b7ede0] transition-colors">
                {t('nav.trips')}
              </Link>
              <Link to="/gallery" className="block text-gray-400 hover:text-[#b7ede0] transition-colors">
                {t('nav.gallery')}
              </Link>
              <Link to="/contact" className="block text-gray-400 hover:text-[#b7ede0] transition-colors">
                {t('nav.contact')}
              </Link>
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-semibold mb-6 font-['Playfair_Display'] text-[#b7ede0]">{t('contact.title')}</h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin size={20} className="text-[#b7ede0] flex-shrink-0 mt-1" />
                <span className="text-gray-400">{t('contact.address')}</span>
              </div>
              <a 
                href="tel:+33644383406" 
                className="flex items-center gap-3 text-gray-400 hover:text-[#b7ede0] transition-colors"
              >
                <Phone size={20} className="text-[#b7ede0]" />
                <span>06 44 38 34 06</span>
              </a>
              <a 
                href="mailto:contact@calvi-fishing.com" 
                className="flex items-center gap-3 text-gray-400 hover:text-[#b7ede0] transition-colors"
              >
                <Envelope size={20} className="text-[#b7ede0]" />
                <span>contact@calvi-fishing.com</span>
              </a>
            </div>
          </div>
        </div>

        {/* Payment & Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-[#b7ede0]/20">
          {/* Secure Payment */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mb-6">
            <div className="flex items-center gap-3">
              <ShieldCheck size={24} className="text-[#b7ede0]" />
              <span className="text-gray-400 text-sm font-medium">Paiement sécurisé par Square</span>
            </div>
            <PaymentIcons />
          </div>
          
          {/* Copyright */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 border-t border-[#b7ede0]/10">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Calvi Fishing. {t('footer.rights')}{' '}
              <a href="https://www.webisula.com/" target="_blank" rel="noopener noreferrer" className="text-[#b7ede0] hover:underline">
                Création WEBISULA
              </a>
            </p>
            <div className="flex gap-6 text-sm">
              <Link to="/legal" className="text-gray-500 hover:text-[#b7ede0] transition-colors">
                {t('footer.legal')}
              </Link>
              <Link to="/cgv" className="text-gray-500 hover:text-[#b7ede0] transition-colors">
                CGV
              </Link>
              <Link to="/privacy" className="text-gray-500 hover:text-[#b7ede0] transition-colors">
                {t('footer.privacy')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
